import { Chip, List } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { ReactNode, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import defaultThumbnail from 'src/images/thumbnail-default.jpg';
import BusyIcon from 'src/js/component/base/busy-icon';
import { triggerEvent, watchEvent } from 'src/js/events';
import { keyFromValue } from 'src/js/helper/enum-helper';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStores } from 'src/js/hook/use-stores';
import streamModels from 'src/js/pages/streaming/stream/stream-store';
import { scrollPanelIntoView } from 'src/js/pages/streaming/streaming-action-bar';
import { constant } from 'src/js/constant';
import { decoderOptionsStreamName } from '../stream/stream-view-model-mapper';

import { DecoderEdit } from './decoder-edit';
import {
  BufferingModeEnum,
  DecoderInfoStateEnum,
  DecoderViewModel,
  decoderIsStopped,
  decoderStateToIcon,
  translateDecoderState,
  decoderStateToHintText,
} from './decoder-model';
import { DecoderStatistics } from './decoder-statistics';
import { startStopDecoder } from './decoder-store';

interface Props {
  model: DecoderViewModel;
  onSelect: (item: DecoderViewModel, selected: boolean) => void;
  scrollToId: number | string;
}

export const sdiOutputs = (model: DecoderViewModel) => {
  const outputs: ReactNode[] = [];
  const push = (output: number) => {
    outputs.push(
      <List.PanelDetailItem
        key={`${output}`}
        text={
          <Chip className="round" size="sm" id={output}>
            {output}
          </Chip>
        }
      />,
    );
  };

  for (let i = 0; i < model.outputs.length; i++) {
    if (model.outputs[i]) {
      push(i + 1);
    }
  }

  return outputs;
};

export const DecoderRow: React.FunctionComponent<Props> = ({ model, onSelect, scrollToId }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const location = useLocation();
  const showStats = location?.state?.stats === true;

  const scroll = location?.state?.id === model.id || scrollToId === model.id;

  watchEvent('expandAll:edit', () => {
    setExpandContent('SETTINGS');
    setIsExpanded(true);
  });
  watchEvent('expandAll:stats', () => {
    setExpandContent('STATISTICS');
    setIsExpanded(true);
  });
  const collapseAll = () => {
    setIsExpanded(false);
    setCollapseTimeoutId(
      setTimeout(() => {
        // the component needs to stay rendered during close for smooth closing
        setExpandContent(null);
        setCollapseTimeoutId(null);
      }, constant.collapseTime),
    );
  };
  watchEvent('collapseAll:edit', collapseAll);
  watchEvent('collapseAll:stats', collapseAll);

  const [isExpanded, setIsExpanded] = useState<boolean>(scroll);
  const [collapseTimeoutId, setCollapseTimeoutId] = useMutable(null);
  const [expandContent, setExpandContent] = useState<string>(scroll ? (showStats ? 'STATISTICS' : 'SETTINGS') : null);
  const onExpandedStateChange = (expandedState: { eventKey: any; expanded: boolean }) => {
    if (isExpanded !== expandedState.expanded) {
      setIsExpanded(expandedState.expanded);
    }
  };

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const onSelectHandler = (eventKey: string, expanded: boolean, ev: KeyboardEvent) => {
    switch (eventKey) {
      case 'STATISTICS':
      case 'SETTINGS':
        if (ev && ev.shiftKey) {
          // expend all
          if (expanded) {
            eventKey === 'SETTINGS' ? triggerEvent('expandAll:edit') : triggerEvent('expandAll:stats');
          } else {
            eventKey === 'SETTINGS' ? triggerEvent('collapseAll:edit') : triggerEvent('collapseAll:stats');
          }
          return;
        }
        setExpandContent(eventKey);
        if (expanded) {
          setIsExpanded(true);
          if (collapseTimeoutId() !== null) {
            clearTimeout(collapseTimeoutId());
            setCollapseTimeoutId(null); // prevent closing of panel
          }
          scroll && scrollPanelIntoView({ id: model.id });
        } else {
          setIsExpanded(false);
          setCollapseTimeoutId(
            setTimeout(() => {
              // the component needs to stay rendered during close for smooth closing
              setExpandContent(null);
              setCollapseTimeoutId(null);
            }, constant.collapseTime),
          );
        }
        break;
      case 'PLAY':
        setIsUpdating(true);
        startStopDecoder(model, decoderIsStopped(model.stats?.state), false, t).then(() => {
          triggerEvent('preset-update');
          setIsUpdating(false);
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    return () => {
      // final cleanup
      if (collapseTimeoutId() !== null) {
        clearTimeout(collapseTimeoutId());
      }
    };
  }, []);

  // thumbnail refresh
  const [thumbnailDate, setThumbnailDate] = useState<string>(`${new Date().getTime()}`);
  useEffect(() => {
    let endrun = false;
    let timer: any;
    const triggerRefresh = () => {
      if (!endrun) {
        setThumbnailDate(`${new Date().getTime()}`);
        if (model.previewIntervalSec >= 1) {
          timer = setTimeout(triggerRefresh, model.previewIntervalSec * 1000);
        }
      }
    };

    timer = setTimeout(() => {
      triggerRefresh();
    }, model.previewIntervalSec * 1000);

    return () => {
      // cleanup
      endrun = true;
      clearTimeout(timer);
    };
  }, [model.previewIntervalSec]);

  watchEvent('thumbnail-update', () => {
    setThumbnailDate(`${new Date().getTime()}`);
  });

  const snapshotRef = useRef<HTMLImageElement>();
  const [defaultThumbnailHidden, setDefaultThumbnailHidden] = useState(false);
  const [realThumbnailHidden, setRealThumbnailHidden] = useState(true);
  const onLoadThumbnail = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    if (e.type !== 'error' && snapshotRef?.current) {
      snapshotRef.current.style.visibility = 'visible';
      setDefaultThumbnailHidden(true);
      setRealThumbnailHidden(false);
      return;
    }
    setDefaultThumbnailHidden(false);
    setRealThumbnailHidden(true);
  };

  useEffect(() => {
    if (model.state !== DecoderInfoStateEnum.STARTED || !model.previewEnabled) {
      setDefaultThumbnailHidden(false);
      setRealThumbnailHidden(true);
    }
  }, [model.state, model.previewEnabled]);

  const btnId = `decoder-settings-${model.id}`;
  const handleCancelEdit = () => {
    const btn = document.getElementById(btnId);
    if (!isNil(btn)) {
      btn.click();
    }
  };

  const stream = streamModels?.find((s) => s.id === model.streamId);
  const statusColor = model.color;
  const resolution = model.stats?.videoInputResolution;
  const bufferingMode = t(`decoder.bufferingModes.${keyFromValue(BufferingModeEnum, model.bufferingMode)}`);
  const bufferingStr =
    model.bufferingMode === BufferingModeEnum.FIXED
      ? `${bufferingMode} ${model.bufferingDelay}ms`
      : model.bufferingMode === BufferingModeEnum.MULTISYNC
      ? `${bufferingMode} ${model.multisyncBufferingDelay}ms`
      : bufferingMode;

  const ref = useRef<HTMLDivElement>();
  const possibleText = decoderStateToHintText(model, t);
  useEffect(() => {
    const panelIconDiv = ref.current?.querySelector('.list-panel-title-wrapper > div > div');
    if (panelIconDiv) {
      if (possibleText) {
        (panelIconDiv as any).title = possibleText;
      } else if (!possibleText && (panelIconDiv as any).title && (panelIconDiv as any).title.length) {
        // cleaning title
        (panelIconDiv as any).title = '';
      }
    }
  }, [model?.stats.state, possibleText]);

  return (
    <div
      id={`panel-${model.id}`}
      className={model.hidden ? 'mk-row-hidden' : ''}
      style={{ scrollMarginTop: '21px' }}
      ref={ref}
    >
      <List.Panel
        onSelect={(selected: boolean) => onSelect(model, selected)}
        checked={model.selected}
        panelColor={statusColor}
        expandedState={{ eventKey: expandContent, expanded: isExpanded }}
        onExpandedStateChange={onExpandedStateChange}
      >
        <List.PanelTitle
          title={model.name}
          statusIcon={decoderStateToIcon(model.stats?.state)}
          iconColor={statusColor}
        />
        {model?.previewEnabled && (
          <List.Thumbnail
            src={`/apis/decoders/${model.id}/preview?t=${thumbnailDate}`}
            onError={onLoadThumbnail}
            onLoad={onLoadThumbnail}
            componentRef={snapshotRef}
            hidden={model.hidden || realThumbnailHidden}
          />
        )}
        <List.Thumbnail
          src={defaultThumbnail}
          hidden={model.hidden || defaultThumbnailHidden}
          overlayText={
            model.state !== DecoderInfoStateEnum.STARTED || !model?.previewEnabled
              ? translateDecoderState(model.stats.state, model.stats.troubleCode, t).toUpperCase()
              : undefined
          }
          overlayStyles={{ backgroundColor: 'transparent' }}
        />
        <List.PanelDetail>
          <List.PanelDetailItem divider="vertical" text={t('general.id')}>
            <Chip size="sm">{model.id.toString()}</Chip>
          </List.PanelDetailItem>
          <List.PanelDetailItem divider="vertical" text={decoderOptionsStreamName(t, stream)} icon="Streams" />
          {resolution && <List.PanelDetailItem divider="vertical" text={resolution} />}
          <List.PanelDetailItem divider={model.nNumOfOutputs > 0 ? 'vertical' : ''} text={bufferingStr} />
          {model.nNumOfOutputs > 0 && <List.PanelDetailItem icon="OutputSDI" />}
          {model.nNumOfOutputs > 0 && sdiOutputs(model)}
        </List.PanelDetail>
        {sessionStore.isUser() ? (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey="STATISTICS"
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey="SETTINGS"
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
          </List.Actions>
        ) : (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey="STATISTICS"
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey="SETTINGS"
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
            <List.ActionItem isDivider />
            <List.ActionItem
              eventKey="PLAY"
              icon={
                <BusyIcon
                  isActive={!decoderIsStopped(model.stats.state)}
                  activeIcon="StopFilled"
                  inactiveIcon="PlayFilled"
                  busy={isUpdating}
                />
              }
              showActive={false}
              onSelect={onSelectHandler}
              disabled={isUpdating}
            />
          </List.Actions>
        )}
        <List.ExpandedPanel>
          {expandContent === 'STATISTICS' && (
            <DecoderStatistics decoder={model} stream={stream} isExpanded={isExpanded} parent="DECODER" />
          )}
          {expandContent === 'SETTINGS' && <DecoderEdit model={model} onCancel={handleCancelEdit} />}
        </List.ExpandedPanel>
      </List.Panel>
    </div>
  );
};
